import { gql, QueryHookOptions, useQuery } from '@apollo/client';
import { toLower, uniqBy } from 'lodash';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../contexts';
import { IntegrationsContext } from '../contexts/IntegrationsContext';
import { SupportedCredentialsQuery } from '../graphql/types';
import Auth from '../lib/Auth';
import { useIntegrationsData } from '../lib/contentfulClient';
import { Integration } from '../lib/contentfulClient/shared';
import { useObject } from '../lib/hooks';

const SUPPORTED_CREDENTIALS = gql`
  query SupportedCredentials {
    supportedCredentials {
      supportedWallets
      exchangeCredentials {
        id
        schema
      }
      reconCredentialTypes
      evmCredentialTypes
      solanaCredentialTypes
      metadata {
        integrationId
        iconUrl
      }
    }
  }
`;

type SupportedIntegration = Omit<Integration, 'id'> & {
  id: NonNullable<Integration['id']>;
};

function useSupportedCredentials(options: QueryHookOptions<SupportedCredentialsQuery>) {
  const res = useQuery<SupportedCredentialsQuery>(SUPPORTED_CREDENTIALS, options);
  return { ...res, data: res.data?.supportedCredentials };
}

const isStage = process.env.ENV === 'stage'; // set in netlify.toml

interface Props {
  children: ReactNode;
}

export default function IntegrationsProvider({ children }: Props) {
  const [page, setPage] = useState(0);
  const [integrations, setIntegrations] = useState<SupportedIntegration[]>([]);
  const [allPagesLoaded, setAllPagesLoaded] = useState(false);
  const { isLoggedIn } = useUserContext();

  const isTokenTaxAdmin = Auth.tokenIsForAdmin();

  const { data: supportedCredentialData, loading: supportedCredentialsLoading } = useSupportedCredentials({
    skip: !isLoggedIn,
    context: { batched: true },
  });

  const {
    data: integrationsData,
    loading: integrationsDataLoading,
    error: integrationsError,
  } = useIntegrationsData({
    fetchPolicy: 'no-cache',
    variables: {
      skip: page * 100,
    },
  });

  const metadata = useMemo(() => {
    return new Map(
      supportedCredentialData?.metadata.map((item) => [item.integrationId, { iconUrl: item.iconUrl }]) ?? [],
    );
  }, [supportedCredentialData]);

  const supportedIntegrations = useMemo(() => {
    const ids = supportedCredentialData
      ? [
          ...supportedCredentialData.supportedWallets,
          ...supportedCredentialData.exchangeCredentials.map((c) => c.id),
        ]
      : [];

    return integrations.filter(({ id, csvInstructions, deprecatedApiIntegration }) => {
      if (deprecatedApiIntegration) return false; // deprecated API integrations are not supported
      if (ids.includes(id)) return true; // include if in supported credentials list
      if (csvInstructions?.instructions) return true; // include if has CSV instructions
      return false;
    });
  }, [integrations, supportedCredentialData]);

  useEffect(() => {
    // Handle error case
    if (integrationsError) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch integrations from Contentful:', integrationsError);
      setAllPagesLoaded(true); // Mark as loaded even on error to prevent infinite loading state
      return;
    }

    // Skip if still loading or no data
    if (integrationsDataLoading || !integrationsData?.integrationCollection) {
      return;
    }

    const items =
      integrationsData.integrationCollection.items.filter(
        (integration): integration is SupportedIntegration => {
          if (!integration) return false;
          const { id, adminOnly, stageOnly } = integration;
          if (adminOnly && !isTokenTaxAdmin) {
            return false;
          }
          if (stageOnly && !isStage) {
            return false;
          }
          return Boolean(id);
        },
      ) || [];

    if (items.length > 0) {
      setIntegrations((prevIntegrationData) => uniqBy([...prevIntegrationData, ...items], 'id'));
    }

    // Check if we need to fetch more pages
    const hasMorePages = integrationsData.integrationCollection.items.length === 100;
    if (hasMorePages) {
      setPage((prev) => prev + 1);
    } else {
      setAllPagesLoaded(true);
    }
  }, [integrationsData, integrationsDataLoading, integrationsError, isTokenTaxAdmin]);

  const reconCredentialTypes = useMemo(
    () => supportedCredentialData?.reconCredentialTypes.map(toLower) ?? [],
    [supportedCredentialData?.reconCredentialTypes],
  );

  const evmCredentialTypes = useMemo(
    () => supportedCredentialData?.evmCredentialTypes.map(toLower) ?? [],
    [supportedCredentialData?.evmCredentialTypes],
  );

  const solanaCredentialTypes = useMemo(
    () => supportedCredentialData?.solanaCredentialTypes.map(toLower) ?? [],
    [supportedCredentialData?.solanaCredentialTypes],
  );

  const isReconCredentialType = useCallback(
    (credentialType: string) => {
      return reconCredentialTypes.includes(credentialType.toLowerCase()) ?? false;
    },
    [reconCredentialTypes],
  );

  // Loading is true until all pages of integrations have been fetched AND supportedCredentials has loaded
  const isLoading = !allPagesLoaded || supportedCredentialsLoading;

  const integrationsById = useMemo(
    () => new Map(integrations.map((integration) => [integration.id, integration])),
    [integrations],
  );

  const context = useObject({
    loading: isLoading,
    allIntegrations: integrations,
    integrationsById,
    supportedIntegrations,
    exchangeCredentials: supportedCredentialData?.exchangeCredentials || [],
    supportedWallets: supportedCredentialData?.supportedWallets || [],
    isReconCredentialType,
    evmCredentialTypes,
    solanaCredentialTypes,
    metadata,
  });

  return <IntegrationsContext.Provider value={context}>{children}</IntegrationsContext.Provider>;
}

export const useIntegrationsContext = () => React.useContext(IntegrationsContext);
